/**
 * ModalWrapper — Shared modal backdrop + close button.
 * ModalCard    — Shared modal card (the dark box inside the backdrop).
 *
 * Rules:
 * - Clicking outside does NOT close the modal
 * - X button always visible, top-right, consistent position
 * - Escape key closes the modal
 * - Dark theme backdrop with configurable blur
 *
 * Usage:
 *   <ModalWrapper visible={open} onClose={handleClose}>
 *     <ModalCard size="md">
 *       <ModalCard.Header>Title</ModalCard.Header>
 *       <ModalCard.Body>Content</ModalCard.Body>
 *     </ModalCard>
 *   </ModalWrapper>
 *
 * Sizes: sm (480px), md (600px), lg (800px), xl (900px)
 */
import React, { useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, ${props => props.$opacity || 0.8});
  ${props => props.$blur ? `backdrop-filter: blur(${props.$blur}px);` : ''}
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: ${props => props.$zIndex || 1000};
  animation: ${fadeIn} 0.15s ease-out;

  /* Samsung Tizen: backdrop-filter is globally disabled in GlobalStyles
   * (caused flicker). Compensate with a darker solid background so the
   * modal still reads as an overlay rather than a translucent panel. */
  html[data-platform='tizen'] & {
    background: rgba(0, 0, 0, 0.94);
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  animation: ${slideUp} 0.2s ease-out;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: white;
    border-color: rgba(255, 255, 255, 0.25);
  }
`;

const ModalWrapper = ({
  visible = true,
  onClose,
  children,
  zIndex = 1000,
  blur = 8,
  opacity = 0.8,
  showCloseButton = true,
  // A tap on the backdrop closes the modal (opt-in: a modal mid-job must not vanish on a stray tap)
  closeOnBackdrop = false,
}) => {
  const { t } = useTranslation();
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (!visible) return;
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [visible, handleEscape]);

  if (!visible) return null;

  return (
    <Backdrop $visible={visible} $zIndex={zIndex} $blur={blur} $opacity={opacity} onClick={closeOnBackdrop && onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}>
      <ContentWrapper>
        {showCloseButton && (
          <CloseButton onClick={onClose} aria-label={t('common.close')}>
            <X size={18} />
          </CloseButton>
        )}
        {children}
      </ContentWrapper>
    </Backdrop>
  );
};

// -- ModalCard: shared card component --

const SIZES = { sm: '480px', md: '600px', lg: '800px', xl: '900px', xxl: '1200px' };

const StyledCard = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 20px;
  width: 92vw;
  max-width: ${props => SIZES[props.$size] || SIZES.md};
  max-height: 90vh;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 28px 20px;
  padding-right: 60px; /* space for X button */
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;

  h2, h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.4;
  }
`;

const CardBody = styled.div`
  padding: 20px 28px 24px;
  flex: 1;
  overflow-y: auto;
  min-height: 0; /* allow flex child to shrink below content size */
`;

const ModalCard = ({ size = 'md', children, className, style }) => (
  <StyledCard $size={size} className={className} style={style}>
    {children}
  </StyledCard>
);

ModalCard.Header = CardHeader;
ModalCard.Body = CardBody;

export default ModalWrapper;
export { ModalCard };
